import React from 'react';
import { motion } from 'framer-motion';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { Users, AlertTriangle, Droplets, Shield, TrendingUp, Factory, Home, Activity, Train, Building2, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';

const COLORS = ['#00FF41', '#FFFFFF', '#008F24', '#333333', '#666666'];

const DEMOGRAPHICS_DATA = [
    { name: 'Mujeres', value: 52.5, count: 212465 },
    { name: 'Hombres', value: 47.5, count: 192230 },
];

const AGE_DISTRIBUTION = [
    { name: '0-14', value: 18.6 },
    { name: '15-24', value: 13.5 },
    { name: '25-34', value: 23.3 },
    { name: '35-59', value: 27.3 },
    { name: '> 60', value: 17.3 },
];

const POVERTY_DATA = [
    { name: 'Pobreza Mod.', value: 37.7, color: '#eab308' },
    { name: 'Pobreza Ext.', value: 2.4, color: '#ef4444' },
    { name: 'Vulnerable', value: 34.7, color: '#3b82f6' },
    { name: 'No Pobre', value: 25.2, color: '#00FF41' },
];

const ELECTION_RESULTS_2024 = [
    { name: 'Lourdes Paz (Morena)', votes: 49.48, fill: '#8B0000' },
    { name: 'Daniel Ordenes (PAN/PRI)', votes: 37.76, fill: '#1E3A8A' },
    { name: 'Jesús Sánchez (MC)', votes: 6.39, fill: '#F97316' },
    { name: 'Rodolfo Ávila (Ind)', votes: 4.10, fill: '#6B7280' },
];

const CRIME_TREND = [
    { year: '2019', crimes: 100 },
    { year: '2020', crimes: 85 },
    { year: '2021', crimes: 72 },
    { year: '2022', crimes: 58 },
    { year: '2023', crimes: 45 },
    { year: '2024', crimes: 42 },
    { year: '2025', crimes: 38 },
];

const PERCEPTION_DATA = [
    { location: 'Cajero Automático', unsafe: 80.0 },
    { location: 'Transporte Público', unsafe: 72.3 },
    { location: 'Percepción General', unsafe: 62.0 },
    { location: 'Conflictos Vecinales', unsafe: 56.4 },
];

// New: Economic data from research
const ECONOMY_DATA = [
    { sector: 'Manufactura', units: 1742, workers: 29853, percentage: 5.6 },
    { sector: 'Comercio', units: 8500, workers: 45000, percentage: 12.3 },
    { sector: 'Servicios', units: 5200, workers: 28000, percentage: 8.1 },
];

const TOP_INDUSTRIES = [
    { name: 'Industria Alimentaria', percentage: 28 },
    { name: 'Productos Metálicos', percentage: 22 },
    { name: 'Impresión y Afines', percentage: 18 },
    { name: 'Farmacéuticos', percentage: 15 },
    { name: 'Otros', percentage: 17 },
];

const WATER_AFFECTED_COLONIES = [
    'Agrícola Oriental',
    'Pantitlán',
    'Gabriel Ramos Millán',
    'Zapata Vela',
    'Campamento 2 de Octubre',
    'Los Reyes',
    'Juventino Rosas',
];

const IztacalcoInfographic = () => {
    return (
        <div className="min-h-screen bg-background text-white font-sans selection:bg-primary selection:text-black pb-20">
            {/* Hero Section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative h-[50vh] flex flex-col justify-center items-center text-center px-4 border-b border-primary/20 overflow-hidden"
            >
                <div className="absolute inset-0 bg-[#00FF41]/5 opacity-20"
                    style={{ backgroundImage: 'radial-gradient(#00FF41 1px, transparent 1px)', backgroundSize: '30px 30px' }}>
                </div>

                <div className="z-10 max-w-5xl">
                    <Link to="/app" className="inline-block mb-4 text-xs font-mono text-zinc-500 hover:text-primary transition-colors">
                        ← Volver al Dashboard
                    </Link>
                    <motion.div
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="mb-6 inline-block px-3 py-1 border border-primary text-primary bg-primary/10 rounded font-mono text-xs uppercase tracking-widest shadow-[0_0_15px_rgba(0,255,65,0.3)]"
                    >
                        Expediente Maestro 2025
                    </motion.div>
                    <h1 className="text-4xl md:text-6xl font-display font-bold mb-4 text-white tracking-tight">
                        Análisis Estratégico <span className="text-primary">Iztacalco</span>
                    </h1>
                    <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
                        Datos verificados de 10+ fuentes oficiales · INEGI, CONEVAL, CDMX, Secretaría de Economía
                    </p>
                </div>
            </motion.div>

            {/* Key Stats Grid */}
            <div className="max-w-7xl mx-auto px-4 -mt-12 relative z-20 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {[
                    { label: 'Población', value: '404,695', sub: 'Habitantes 2020', icon: Users },
                    { label: 'Densidad', value: '17,523', sub: 'hab/km²', icon: Activity },
                    { label: 'Superficie', value: '23.1', sub: 'km²', icon: Home },
                    { label: 'Pobreza', value: '23.6%', sub: 'Multidimensional', icon: AlertTriangle, alert: true },
                    { label: 'Manufactura', value: '#2', sub: 'en CDMX', icon: Factory },
                    { label: 'Presupuesto', value: '$853M', sub: 'MXN 2024', icon: Briefcase },
                ].map((stat, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className={`bg-surface/90 backdrop-blur-md p-5 rounded-xl border ${stat.alert ? 'border-red-900/50' : 'border-zinc-800'}`}
                    >
                        <stat.icon className={`w-5 h-5 mb-3 ${stat.alert ? 'text-red-500' : 'text-primary'}`} />
                        <div className={`text-2xl font-mono font-bold ${stat.alert ? 'text-red-500' : 'text-white'}`}>{stat.value}</div>
                        <div className="text-xs text-zinc-400 mt-1">{stat.label}</div>
                        <div className="text-[10px] text-zinc-600 font-mono">{stat.sub}</div>
                    </motion.div>
                ))}
            </div>

            <div className="max-w-6xl mx-auto mt-20 space-y-24 px-4">

                {/* Section 1: Demographics */}
                <section>
                    <div className="mb-8 pl-4 border-l-2 border-primary">
                        <h3 className="text-2xl font-display font-bold text-white">Arquitectura Demográfica</h3>
                        <p className="text-zinc-500 font-mono text-sm mt-1">Población madura con ventana económica crítica</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-surface border border-zinc-800 p-6 rounded-xl">
                            <h4 className="text-lg font-bold mb-4 text-white">Distribución por Sexo</h4>
                            <div className="h-56">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={DEMOGRAPHICS_DATA} cx="50%" cy="50%" innerRadius={50} outerRadius={70} paddingAngle={5} dataKey="value" stroke="none">
                                            {DEMOGRAPHICS_DATA.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={index === 0 ? '#FB7185' : '#00FF41'} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={{ backgroundColor: '#181818', borderColor: '#333' }} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                <div className="p-3 bg-zinc-900/50 rounded-lg text-center">
                                    <div className="text-xl font-mono font-bold text-white">91.6</div>
                                    <div className="text-[10px] text-zinc-500 font-mono">Hombres/100 Mujeres</div>
                                </div>
                                <div className="p-3 bg-zinc-900/50 rounded-lg text-center">
                                    <div className="text-xl font-mono font-bold text-white">35</div>
                                    <div className="text-[10px] text-zinc-500 font-mono">Edad Mediana</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-surface border border-zinc-800 p-6 rounded-xl">
                            <h4 className="text-lg font-bold mb-4 text-white">Pirámide Etaria</h4>
                            <div className="h-56">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={AGE_DISTRIBUTION} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" horizontal={false} />
                                        <XAxis type="number" stroke="#666" tick={{ fontFamily: 'monospace', fontSize: 10 }} />
                                        <YAxis dataKey="name" type="category" stroke="#999" width={40} tick={{ fontFamily: 'monospace', fontSize: 10 }} />
                                        <Tooltip contentStyle={{ backgroundColor: '#181818', borderColor: '#333' }} />
                                        <Bar dataKey="value" fill="#00FF41" radius={[0, 4, 4, 0]} name="% Población" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-4 p-3 bg-primary/10 border border-primary/30 rounded-lg">
                                <p className="text-xs text-zinc-400 font-mono">
                                    Cohorte <span className="text-primary font-bold">25-34 años = 23.3%</span>. Demanda crítica de vivienda y empleo formal.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 2: Economy - NEW */}
                <section>
                    <div className="mb-8 pl-4 border-l-2 border-emerald-500">
                        <h3 className="text-2xl font-display font-bold text-white">Motor Económico</h3>
                        <p className="text-zinc-500 font-mono text-sm mt-1">2° centro industrial de CDMX · Granjas México y Agrícola Oriental</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-surface border border-zinc-800 p-6 rounded-xl">
                            <Factory className="w-8 h-8 text-emerald-400 mb-4" />
                            <div className="text-3xl font-mono font-bold text-white">1,742</div>
                            <div className="text-sm text-zinc-400 mt-1">Unidades Manufactureras</div>
                            <div className="text-xs text-zinc-600 font-mono">5.6% del total CDMX</div>
                            <div className="mt-4 p-3 bg-emerald-900/20 border border-emerald-500/30 rounded-lg">
                                <div className="text-lg font-mono font-bold text-emerald-400">29,853</div>
                                <div className="text-xs text-zinc-500">Empleos manufactura</div>
                            </div>
                        </div>

                        <div className="bg-surface border border-zinc-800 p-6 rounded-xl">
                            <h4 className="text-lg font-bold mb-4 text-white">Sectores Principales</h4>
                            <div className="space-y-3">
                                {TOP_INDUSTRIES.map((ind, i) => (
                                    <div key={i}>
                                        <div className="flex justify-between text-xs font-mono mb-1">
                                            <span className="text-zinc-300">{ind.name}</span>
                                            <span className="text-emerald-400">{ind.percentage}%</span>
                                        </div>
                                        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                                            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${ind.percentage}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-surface border border-zinc-800 p-6 rounded-xl">
                            <Building2 className="w-8 h-8 text-blue-400 mb-4" />
                            <div className="text-3xl font-mono font-bold text-white">$146M</div>
                            <div className="text-sm text-zinc-400 mt-1">Exportaciones 2024</div>
                            <div className="text-xs text-zinc-600 font-mono">USD · -1.93% vs 2023</div>
                            <div className="mt-4 space-y-2">
                                <div className="text-xs text-zinc-500 font-mono">Principales destinos:</div>
                                <div className="flex flex-wrap gap-1">
                                    {['EE.UU.', 'Panamá', 'Guatemala'].map((c, i) => (
                                        <span key={i} className="px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-300">{c}</span>
                                    ))}
                                </div>
                            </div>
                            <p className="mt-4 text-xs text-zinc-500 font-mono">
                                Empresas: Coca-Cola, Benotto (bicicletas), farmacéuticas
                            </p>
                        </div>
                    </div>
                </section>

                {/* Section 3: Security */}
                <section>
                    <div className="mb-8 pl-4 border-l-2 border-red-600">
                        <h3 className="text-2xl font-display font-bold text-white">Seguridad Pública</h3>
                        <p className="text-zinc-500 font-mono text-sm mt-1">Q1 2024: 337 delitos de alto impacto · Cifra negra 92.9%</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-surface border border-zinc-800 p-6 rounded-xl">
                            <div className="flex items-center gap-3 mb-6">
                                <Shield className="text-primary" />
                                <h4 className="text-lg font-bold text-white">Tendencia Delictiva</h4>
                            </div>
                            <div className="h-48">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={CRIME_TREND}>
                                        <defs>
                                            <linearGradient id="colorCrimes" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#00FF41" stopOpacity={0.8} />
                                                <stop offset="95%" stopColor="#00FF41" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                                        <XAxis dataKey="year" stroke="#666" tick={{ fontFamily: 'monospace', fontSize: 10 }} />
                                        <YAxis stroke="#666" tick={{ fontFamily: 'monospace', fontSize: 10 }} />
                                        <Tooltip contentStyle={{ backgroundColor: '#181818', borderColor: '#333' }} />
                                        <Area type="monotone" dataKey="crimes" stroke="#00FF41" fillOpacity={1} fill="url(#colorCrimes)" name="Índice" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-4">
                                <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg text-center">
                                    <span className="text-2xl font-mono font-bold text-primary">-62%</span>
                                    <p className="text-xs text-zinc-500 mt-1">2019-2025</p>
                                </div>
                                <div className="p-3 bg-zinc-900/50 rounded-lg text-center">
                                    <span className="text-2xl font-mono font-bold text-white">18%</span>
                                    <p className="text-xs text-zinc-500 mt-1">Reducción 2024</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-surface border border-red-900/30 p-6 rounded-xl">
                            <div className="flex items-center gap-3 mb-6">
                                <AlertTriangle className="text-red-500" />
                                <h4 className="text-lg font-bold text-white">Percepción de Inseguridad</h4>
                            </div>
                            <div className="space-y-4">
                                {PERCEPTION_DATA.map((item, idx) => (
                                    <div key={idx}>
                                        <div className="flex justify-between text-sm font-mono mb-2">
                                            <span className="text-zinc-300">{item.location}</span>
                                            <span className="text-red-500 font-bold">{item.unsafe}%</span>
                                        </div>
                                        <div className="w-full bg-zinc-900 h-2 rounded-full overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                whileInView={{ width: `${item.unsafe}%` }}
                                                transition={{ duration: 1 }}
                                                className="bg-red-600 h-full"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 p-3 bg-red-950/30 border border-red-900/50 rounded-lg">
                                <p className="text-xs text-red-200/80 font-mono">
                                    ⚠ Colonias críticas: Agrícola Oriental, Pantitlán, Campamento 2 de Octubre, Juventino Rosas
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 4: Water Crisis - Enhanced */}
                <section>
                    <div className="mb-8 pl-4 border-l-2 border-cyan-500">
                        <h3 className="text-2xl font-display font-bold text-white">Crisis Hídrica</h3>
                        <p className="text-zinc-500 font-mono text-sm mt-1">Infraestructura del Porfiriato · 30% pérdida por fugas</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-cyan-900/30 to-zinc-900 border border-cyan-800/30 p-6 rounded-xl">
                            <Droplets className="w-10 h-10 text-cyan-400 mb-4" />
                            <h4 className="text-xl font-bold text-white mb-4">Colonias Afectadas</h4>
                            <div className="grid grid-cols-2 gap-2">
                                {WATER_AFFECTED_COLONIES.map((col, i) => (
                                    <div key={i} className="flex items-center gap-2 text-sm text-cyan-100/80">
                                        <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full" />
                                        {col}
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 space-y-3">
                                <div className="flex justify-between p-3 bg-zinc-900/50 rounded-lg">
                                    <span className="text-sm text-zinc-400">Costo pipa privada</span>
                                    <span className="text-sm font-mono font-bold text-white">$3,800 MXN</span>
                                </div>
                                <div className="flex justify-between p-3 bg-zinc-900/50 rounded-lg">
                                    <span className="text-sm text-zinc-400">Frecuencia suministro</span>
                                    <span className="text-sm font-mono font-bold text-red-400">Cada 15 días</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-surface border border-zinc-800 p-6 rounded-xl">
                            <h4 className="text-lg font-bold text-white mb-4">Causas Estructurales</h4>
                            <div className="space-y-4">
                                {[
                                    { issue: 'Infraestructura obsoleta', desc: 'Red hidráulica del Porfiriato' },
                                    { issue: 'Pérdidas por fugas', desc: '30% del agua extraída se pierde' },
                                    { issue: 'Sobreexplotación acuíferos', desc: 'Pozos agotados por extracción excesiva' },
                                    { issue: 'Falta de inversión', desc: 'Décadas sin modernización' },
                                    { issue: 'Contaminación', desc: 'Reportes de agua con olor a gasolina' },
                                ].map((item, i) => (
                                    <div key={i} className="p-3 bg-zinc-900/50 rounded-lg border-l-2 border-cyan-500">
                                        <div className="text-sm font-bold text-white">{item.issue}</div>
                                        <div className="text-xs text-zinc-500">{item.desc}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 5: Transport - NEW */}
                <section>
                    <div className="mb-8 pl-4 border-l-2 border-orange-500">
                        <h3 className="text-2xl font-display font-bold text-white">Conectividad</h3>
                        <p className="text-zinc-500 font-mono text-sm mt-1">3 líneas de Metro · 2 líneas de Metrobús</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-surface border border-zinc-800 p-6 rounded-xl">
                            <Train className="w-8 h-8 text-orange-400 mb-4" />
                            <h4 className="text-lg font-bold text-white mb-4">Metro</h4>
                            <div className="space-y-2">
                                {[
                                    { line: 'Línea 4', stations: 'Santa Anita' },
                                    { line: 'Línea 8', stations: 'Iztacalco, Apatlaco, Coyuya' },
                                    { line: 'Línea 9', stations: 'Cerca de límites' },
                                ].map((l, i) => (
                                    <div key={i} className="p-3 bg-zinc-900/50 rounded-lg">
                                        <div className="text-sm font-bold text-orange-400">{l.line}</div>
                                        <div className="text-xs text-zinc-500">{l.stations}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-surface border border-zinc-800 p-6 rounded-xl">
                            <h4 className="text-lg font-bold text-white mb-4">Metrobús</h4>
                            <div className="space-y-3">
                                <div className="p-3 bg-zinc-900/50 rounded-lg">
                                    <div className="text-sm font-bold text-orange-400">Línea 2</div>
                                    <div className="text-xs text-zinc-500">La Viga, Coyuya, Iztacalco, UPIICSA, Río Frío</div>
                                </div>
                                <div className="p-3 bg-zinc-900/50 rounded-lg">
                                    <div className="text-sm font-bold text-orange-400">Línea 5</div>
                                    <div className="text-xs text-zinc-500">Estación Metro Coyuya (conexión L8)</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-surface border border-zinc-800 p-6 rounded-xl">
                            <h4 className="text-lg font-bold text-white mb-4">Vialidades</h4>
                            <div className="space-y-2 text-sm">
                                {['Viaducto Río de la Piedad', 'Circuito Interior', 'Calzada Ignacio Zaragoza', 'Eje 3 Sur / Eje 5 Sur'].map((v, i) => (
                                    <div key={i} className="flex items-center gap-2 text-zinc-300">
                                        <span className="w-1.5 h-1.5 bg-orange-400 rounded-full" />
                                        {v}
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
                                <p className="text-xs text-orange-200 font-mono">
                                    Plan: Trolebús Iztacalco-Villa de Cortés (conexión L2, L8, Metrobús 5)
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Section 6: Political Landscape */}
                <section className="pb-16">
                    <div className="mb-8 pl-4 border-l-2 border-purple-500">
                        <h3 className="text-2xl font-display font-bold text-white">Genealogía Política 2024</h3>
                        <p className="text-zinc-500 font-mono text-sm mt-1">Morena retiene con 49.48% vs oposición consolidada</p>
                    </div>

                    <div className="bg-surface border border-zinc-800 p-6 rounded-xl">
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={ELECTION_RESULTS_2024}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                                    <XAxis dataKey="name" stroke="#666" tick={{ fontFamily: 'monospace', fontSize: 9 }} tickFormatter={(val) => val.split(' ')[0]} />
                                    <YAxis stroke="#666" tick={{ fontFamily: 'monospace', fontSize: 10 }} />
                                    <Tooltip contentStyle={{ backgroundColor: '#181818', borderColor: '#333' }} />
                                    <Bar dataKey="votes" name="% Votos">
                                        {ELECTION_RESULTS_2024.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-purple-900/10 border border-purple-900/30 rounded-lg">
                                <h5 className="text-purple-400 font-bold mb-2">Alcaldesa: Lourdes Paz</h5>
                                <p className="text-sm text-zinc-400 font-mono">
                                    +200 policías (de 37), +40 patrullas, -18% delitos alto impacto
                                </p>
                            </div>
                            <div className="p-4 bg-blue-900/10 border border-blue-900/30 rounded-lg">
                                <h5 className="text-blue-400 font-bold mb-2">Oposición: 37.76%</h5>
                                <p className="text-sm text-zinc-400 font-mono">
                                    Descontento por agua e inseguridad. Ya no es distrito seguro.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

            </div>

            {/* Footer */}
            <div className="border-t border-zinc-800 py-8 text-center">
                <p className="text-xs text-zinc-600 font-mono">
                    Fuentes: INEGI Censo 2020 · CONEVAL 2022 · CDMX Open Data · Secretaría de Economía · IECM 2024
                </p>
                <Link to="/app" className="inline-block mt-4 text-primary text-sm font-mono hover:underline">
                    ← Volver al Dashboard
                </Link>
            </div>
        </div>
    );
};

export default IztacalcoInfographic;
